// Steer, then queue: messages sent while Poneva works. The composer's words
// while an answer runs, a waiting message's status and actions, and the line
// under a message Poneva read mid-work.
//
// Namespace `steer`, in all 24 app languages. Placeholders are `{name}`, filled
// with `formatCopy`. See ./index.ts.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  placeholder: 'Add something, or change direction…',
  addToWork: 'Add to what Poneva is doing',
  attachmentsWait: 'Attachments go with your next message, when Poneva is done',
  waitingLabel: 'Messages waiting for Poneva',
  readNextStep: 'Poneva reads this at its next step',
  readNextStepMany: 'Poneva reads these at its next step',
  goesNext: 'Goes next, when this answer is done',
  goesNextMany: 'These go next, when this answer is done',
  sending: 'Sending',
  notSent: 'Not sent',
  heldQuestion: 'Waits until you answer the question',
  heldApproval: 'Waits until you decide on the change',
  heldCredits: 'Not sent. You’re out of credits.',
  heldFailed: 'Not sent. The answer stopped with an error.',
  sendNow: 'Send now',
  sendNowHint: 'Stop Poneva and send now',
  send: 'Send',
  sendAnyway: 'Send anyway',
  clear: 'Clear',
  edit: 'Edit',
  remove: 'Remove',
  seePlans: 'See plans',
  added: 'Added while Poneva worked',
} as const;
export type SteerCopyKey = keyof typeof en;

const steerCopy: NamespaceCatalog<SteerCopyKey> = {
  en,
  bg: {
    placeholder: 'Добавете нещо или сменете посоката…',
    addToWork: 'Добавете към това, което Poneva прави',
    attachmentsWait:
      'Прикачените файлове тръгват със следващото ви съобщение, когато Poneva приключи',
    waitingLabel: 'Съобщения, които чакат Poneva',
    readNextStep: 'Poneva ще прочете това на следващата си стъпка',
    readNextStepMany: 'Poneva ще прочете тези на следващата си стъпка',
    goesNext: 'Тръгва следващо, когато този отговор е готов',
    goesNextMany: 'Тези тръгват следващи, когато този отговор е готов',
    sending: 'Изпращане',
    notSent: 'Не е изпратено',
    heldQuestion: 'Чака, докато отговорите на въпроса',
    heldApproval: 'Чака, докато решите за промяната',
    heldCredits: 'Не е изпратено. Нямате кредити.',
    heldFailed: 'Не е изпратено. Отговорът спря с грешка.',
    sendNow: 'Изпрати сега',
    sendNowHint: 'Спрете Poneva и изпратете сега',
    send: 'Изпрати',
    sendAnyway: 'Изпрати въпреки това',
    clear: 'Изчисти',
    edit: 'Редактирай',
    remove: 'Премахни',
    seePlans: 'Вижте плановете',
    added: 'Добавено, докато Poneva работеше',
  },
  hr: {
    placeholder: 'Dodajte nešto ili promijenite smjer…',
    addToWork: 'Dodajte onome što Poneva radi',
    attachmentsWait: 'Privici idu s vašom sljedećom porukom, kad Poneva završi',
    waitingLabel: 'Poruke koje čekaju Ponevu',
    readNextStep: 'Poneva će ovo pročitati u sljedećem koraku',
    readNextStepMany: 'Poneva će ovo pročitati u sljedećem koraku',
    goesNext: 'Šalje se sljedeće, kad ovaj odgovor bude gotov',
    goesNextMany: 'Šalju se sljedeće, kad ovaj odgovor bude gotov',
    sending: 'Slanje',
    notSent: 'Nije poslano',
    heldQuestion: 'Čeka dok ne odgovorite na pitanje',
    heldApproval: 'Čeka dok ne odlučite o promjeni',
    heldCredits: 'Nije poslano. Nemate više kredita.',
    heldFailed: 'Nije poslano. Odgovor se zaustavio zbog pogreške.',
    sendNow: 'Pošalji sada',
    sendNowHint: 'Zaustavite Ponevu i pošaljite sada',
    send: 'Pošalji',
    sendAnyway: 'Ipak pošalji',
    clear: 'Očisti',
    edit: 'Uredi',
    remove: 'Ukloni',
    seePlans: 'Pogledajte planove',
    added: 'Dodano dok je Poneva radila',
  },
  cs: {
    placeholder: 'Přidejte něco nebo změňte směr…',
    addToWork: 'Přidat k tomu, co Poneva dělá',
    attachmentsWait: 'Přílohy odejdou s vaší další zprávou, až Poneva skončí',
    waitingLabel: 'Zprávy čekající na Ponevu',
    readNextStep: 'Poneva si to přečte v dalším kroku',
    readNextStepMany: 'Poneva si je přečte v dalším kroku',
    goesNext: 'Odejde jako další, až bude tato odpověď hotová',
    goesNextMany: 'Odejdou jako další, až bude tato odpověď hotová',
    sending: 'Odesílání',
    notSent: 'Neodesláno',
    heldQuestion: 'Čeká, dokud neodpovíte na otázku',
    heldApproval: 'Čeká, dokud nerozhodnete o změně',
    heldCredits: 'Neodesláno. Došly vám kredity.',
    heldFailed: 'Neodesláno. Odpověď skončila chybou.',
    sendNow: 'Odeslat hned',
    sendNowHint: 'Zastavit Ponevu a odeslat hned',
    send: 'Odeslat',
    sendAnyway: 'Přesto odeslat',
    clear: 'Vymazat',
    edit: 'Upravit',
    remove: 'Odebrat',
    seePlans: 'Zobrazit plány',
    added: 'Přidáno, zatímco Poneva pracovala',
  },
  da: {
    placeholder: 'Tilføj noget, eller skift retning…',
    addToWork: 'Føj til det, Poneva er i gang med',
    attachmentsWait: 'Vedhæftede filer sendes med din næste besked, når Poneva er færdig',
    waitingLabel: 'Beskeder, der venter på Poneva',
    readNextStep: 'Poneva læser dette i sit næste trin',
    readNextStepMany: 'Poneva læser disse i sit næste trin',
    goesNext: 'Sendes som det næste, når dette svar er færdigt',
    goesNextMany: 'Sendes som de næste, når dette svar er færdigt',
    sending: 'Sender',
    notSent: 'Ikke sendt',
    heldQuestion: 'Venter, indtil du har svaret på spørgsmålet',
    heldApproval: 'Venter, indtil du har besluttet dig om ændringen',
    heldCredits: 'Ikke sendt. Du har ikke flere credits.',
    heldFailed: 'Ikke sendt. Svaret stoppede med en fejl.',
    sendNow: 'Send nu',
    sendNowHint: 'Stop Poneva, og send nu',
    send: 'Send',
    sendAnyway: 'Send alligevel',
    clear: 'Ryd',
    edit: 'Rediger',
    remove: 'Fjern',
    seePlans: 'Se abonnementer',
    added: 'Tilføjet, mens Poneva arbejdede',
  },
  nl: {
    placeholder: 'Voeg iets toe of verander van richting…',
    addToWork: 'Toevoegen aan wat Poneva aan het doen is',
    attachmentsWait: 'Bijlagen gaan mee met je volgende bericht, als Poneva klaar is',
    waitingLabel: 'Berichten die op Poneva wachten',
    readNextStep: 'Poneva leest dit bij de volgende stap',
    readNextStepMany: 'Poneva leest deze bij de volgende stap',
    goesNext: 'Gaat als volgende, als dit antwoord klaar is',
    goesNextMany: 'Gaan als volgende, als dit antwoord klaar is',
    sending: 'Versturen',
    notSent: 'Niet verstuurd',
    heldQuestion: 'Wacht tot je de vraag hebt beantwoord',
    heldApproval: 'Wacht tot je over de wijziging hebt beslist',
    heldCredits: 'Niet verstuurd. Je tegoed is op.',
    heldFailed: 'Niet verstuurd. Het antwoord stopte met een fout.',
    sendNow: 'Nu versturen',
    sendNowHint: 'Poneva stoppen en nu versturen',
    send: 'Versturen',
    sendAnyway: 'Toch versturen',
    clear: 'Wissen',
    edit: 'Bewerken',
    remove: 'Verwijderen',
    seePlans: 'Abonnementen bekijken',
    added: 'Toegevoegd terwijl Poneva bezig was',
  },
  et: {
    placeholder: 'Lisa midagi või muuda suunda…',
    addToWork: 'Lisa sellele, mida Poneva teeb',
    attachmentsWait: 'Manused lähevad koos sinu järgmise sõnumiga, kui Poneva on valmis',
    waitingLabel: 'Ponevat ootavad sõnumid',
    readNextStep: 'Poneva loeb selle järgmisel sammul',
    readNextStepMany: 'Poneva loeb need järgmisel sammul',
    goesNext: 'Läheb järgmisena, kui see vastus on valmis',
    goesNextMany: 'Lähevad järgmisena, kui see vastus on valmis',
    sending: 'Saatmine',
    notSent: 'Saatmata',
    heldQuestion: 'Ootab, kuni vastad küsimusele',
    heldApproval: 'Ootab, kuni otsustad muudatuse üle',
    heldCredits: 'Saatmata. Sinu krediidid on otsas.',
    heldFailed: 'Saatmata. Vastus peatus veaga.',
    sendNow: 'Saada kohe',
    sendNowHint: 'Peata Poneva ja saada kohe',
    send: 'Saada',
    sendAnyway: 'Saada ikkagi',
    clear: 'Tühjenda',
    edit: 'Muuda',
    remove: 'Eemalda',
    seePlans: 'Vaata pakette',
    added: 'Lisatud, kui Poneva töötas',
  },
  fi: {
    placeholder: 'Lisää jotain tai vaihda suuntaa…',
    addToWork: 'Lisää siihen, mitä Poneva tekee',
    attachmentsWait: 'Liitteet lähtevät seuraavan viestisi mukana, kun Poneva on valmis',
    waitingLabel: 'Ponevaa odottavat viestit',
    readNextStep: 'Poneva lukee tämän seuraavassa vaiheessaan',
    readNextStepMany: 'Poneva lukee nämä seuraavassa vaiheessaan',
    goesNext: 'Lähtee seuraavaksi, kun tämä vastaus on valmis',
    goesNextMany: 'Lähtevät seuraavaksi, kun tämä vastaus on valmis',
    sending: 'Lähetetään',
    notSent: 'Ei lähetetty',
    heldQuestion: 'Odottaa, kunnes vastaat kysymykseen',
    heldApproval: 'Odottaa, kunnes päätät muutoksesta',
    heldCredits: 'Ei lähetetty. Krediittisi ovat lopussa.',
    heldFailed: 'Ei lähetetty. Vastaus pysähtyi virheeseen.',
    sendNow: 'Lähetä nyt',
    sendNowHint: 'Pysäytä Poneva ja lähetä nyt',
    send: 'Lähetä',
    sendAnyway: 'Lähetä silti',
    clear: 'Tyhjennä',
    edit: 'Muokkaa',
    remove: 'Poista',
    seePlans: 'Katso tilaukset',
    added: 'Lisätty Ponevan työskennellessä',
  },
  fr: {
    placeholder: 'Ajoutez quelque chose ou changez de direction…',
    addToWork: 'Ajouter à ce que fait Poneva',
    attachmentsWait:
      'Les pièces jointes partiront avec votre prochain message, quand Poneva aura fini',
    waitingLabel: 'Messages en attente pour Poneva',
    readNextStep: 'Poneva lira ceci à sa prochaine étape',
    readNextStepMany: 'Poneva lira ces messages à sa prochaine étape',
    goesNext: 'Part ensuite, quand cette réponse sera terminée',
    goesNextMany: 'Partent ensuite, quand cette réponse sera terminée',
    sending: 'Envoi',
    notSent: 'Non envoyé',
    heldQuestion: 'Attend que vous répondiez à la question',
    heldApproval: 'Attend votre décision sur la modification',
    heldCredits: 'Non envoyé. Vous n’avez plus de crédits.',
    heldFailed: 'Non envoyé. La réponse s’est arrêtée sur une erreur.',
    sendNow: 'Envoyer maintenant',
    sendNowHint: 'Arrêter Poneva et envoyer maintenant',
    send: 'Envoyer',
    sendAnyway: 'Envoyer quand même',
    clear: 'Effacer',
    edit: 'Modifier',
    remove: 'Retirer',
    seePlans: 'Voir les forfaits',
    added: 'Ajouté pendant que Poneva travaillait',
  },
  de: {
    placeholder: 'Etwas ergänzen oder die Richtung ändern…',
    addToWork: 'Zu dem hinzufügen, woran Poneva gerade arbeitet',
    attachmentsWait: 'Anhänge gehen mit Ihrer nächsten Nachricht mit, wenn Poneva fertig ist',
    waitingLabel: 'Nachrichten, die auf Poneva warten',
    readNextStep: 'Poneva liest das beim nächsten Schritt',
    readNextStepMany: 'Poneva liest diese beim nächsten Schritt',
    goesNext: 'Geht als Nächstes raus, wenn diese Antwort fertig ist',
    goesNextMany: 'Gehen als Nächstes raus, wenn diese Antwort fertig ist',
    sending: 'Wird gesendet',
    notSent: 'Nicht gesendet',
    heldQuestion: 'Wartet, bis Sie die Frage beantworten',
    heldApproval: 'Wartet, bis Sie über die Änderung entscheiden',
    heldCredits: 'Nicht gesendet. Ihre Credits sind aufgebraucht.',
    heldFailed: 'Nicht gesendet. Die Antwort brach mit einem Fehler ab.',
    sendNow: 'Jetzt senden',
    sendNowHint: 'Poneva anhalten und jetzt senden',
    send: 'Senden',
    sendAnyway: 'Trotzdem senden',
    clear: 'Leeren',
    edit: 'Bearbeiten',
    remove: 'Entfernen',
    seePlans: 'Tarife ansehen',
    added: 'Hinzugefügt, während Poneva arbeitete',
  },
  el: {
    placeholder: 'Προσθέστε κάτι ή αλλάξτε κατεύθυνση…',
    addToWork: 'Προσθήκη σε αυτό που κάνει το Poneva',
    attachmentsWait: 'Τα συνημμένα φεύγουν με το επόμενο μήνυμά σας, όταν τελειώσει το Poneva',
    waitingLabel: 'Μηνύματα που περιμένουν το Poneva',
    readNextStep: 'Το Poneva θα το διαβάσει στο επόμενο βήμα του',
    readNextStepMany: 'Το Poneva θα τα διαβάσει στο επόμενο βήμα του',
    goesNext: 'Φεύγει μετά, όταν ολοκληρωθεί αυτή η απάντηση',
    goesNextMany: 'Φεύγουν μετά, όταν ολοκληρωθεί αυτή η απάντηση',
    sending: 'Αποστολή',
    notSent: 'Δεν στάλθηκε',
    heldQuestion: 'Περιμένει μέχρι να απαντήσετε στην ερώτηση',
    heldApproval: 'Περιμένει μέχρι να αποφασίσετε για την αλλαγή',
    heldCredits: 'Δεν στάλθηκε. Δεν έχετε άλλα credits.',
    heldFailed: 'Δεν στάλθηκε. Η απάντηση σταμάτησε με σφάλμα.',
    sendNow: 'Αποστολή τώρα',
    sendNowHint: 'Σταματήστε το Poneva και στείλτε τώρα',
    send: 'Αποστολή',
    sendAnyway: 'Αποστολή παρ’ όλα αυτά',
    clear: 'Εκκαθάριση',
    edit: 'Επεξεργασία',
    remove: 'Αφαίρεση',
    seePlans: 'Δείτε τα πλάνα',
    added: 'Προστέθηκε ενώ το Poneva δούλευε',
  },
  hu: {
    placeholder: 'Adjon hozzá valamit, vagy váltson irányt…',
    addToWork: 'Hozzáadás ahhoz, amin a Poneva dolgozik',
    attachmentsWait: 'A mellékletek a következő üzenetével mennek, amikor a Poneva végzett',
    waitingLabel: 'A Ponevára váró üzenetek',
    readNextStep: 'A Poneva a következő lépésénél olvassa el',
    readNextStepMany: 'A Poneva a következő lépésénél olvassa el ezeket',
    goesNext: 'Következőként megy, amikor ez a válasz elkészült',
    goesNextMany: 'Következőként mennek, amikor ez a válasz elkészült',
    sending: 'Küldés',
    notSent: 'Nincs elküldve',
    heldQuestion: 'Vár, amíg nem válaszol a kérdésre',
    heldApproval: 'Vár, amíg nem dönt a módosításról',
    heldCredits: 'Nincs elküldve. Elfogytak a kreditjei.',
    heldFailed: 'Nincs elküldve. A válasz hibával leállt.',
    sendNow: 'Küldés most',
    sendNowHint: 'A Poneva leállítása és küldés most',
    send: 'Küldés',
    sendAnyway: 'Küldés mégis',
    clear: 'Törlés',
    edit: 'Szerkesztés',
    remove: 'Eltávolítás',
    seePlans: 'Csomagok megtekintése',
    added: 'Hozzáadva, miközben a Poneva dolgozott',
  },
  ga: {
    placeholder: 'Cuir rud éigin leis, nó athraigh treo…',
    addToWork: 'Cuir leis an méid atá Poneva a dhéanamh',
    attachmentsWait:
      'Imeoidh ceangaltáin le do chéad teachtaireacht eile, nuair a bheidh Poneva réidh',
    waitingLabel: 'Teachtaireachtaí atá ag fanacht le Poneva',
    readNextStep: 'Léifidh Poneva é seo ag a chéad chéim eile',
    readNextStepMany: 'Léifidh Poneva iad seo ag a chéad chéim eile',
    goesNext: 'Imeoidh sé seo ina dhiaidh, nuair a bheidh an freagra seo críochnaithe',
    goesNextMany: 'Imeoidh siad seo ina dhiaidh, nuair a bheidh an freagra seo críochnaithe',
    sending: 'Á sheoladh',
    notSent: 'Níor seoladh',
    heldQuestion: 'Ag fanacht go dtabharfaidh tú freagra ar an gceist',
    heldApproval: 'Ag fanacht go gcinnfidh tú faoin athrú',
    heldCredits: 'Níor seoladh. Níl creidmheasanna ar bith fágtha agat.',
    heldFailed: 'Níor seoladh. Stop an freagra le hearráid.',
    sendNow: 'Seol anois',
    sendNowHint: 'Stop Poneva agus seol anois',
    send: 'Seol',
    sendAnyway: 'Seol mar sin féin',
    clear: 'Glan',
    edit: 'Cuir in eagar',
    remove: 'Bain',
    seePlans: 'Féach ar na pleananna',
    added: 'Curtha leis agus Poneva ag obair',
  },
  it: {
    placeholder: 'Aggiungi qualcosa o cambia direzione…',
    addToWork: 'Aggiungi a ciò che Poneva sta facendo',
    attachmentsWait: 'Gli allegati partono con il tuo prossimo messaggio, quando Poneva ha finito',
    waitingLabel: 'Messaggi in attesa di Poneva',
    readNextStep: 'Poneva lo leggerà al prossimo passaggio',
    readNextStepMany: 'Poneva li leggerà al prossimo passaggio',
    goesNext: 'Parte dopo, quando questa risposta è finita',
    goesNextMany: 'Partono dopo, quando questa risposta è finita',
    sending: 'Invio',
    notSent: 'Non inviato',
    heldQuestion: 'Attende che tu risponda alla domanda',
    heldApproval: 'Attende che tu decida sulla modifica',
    heldCredits: 'Non inviato. Hai esaurito i crediti.',
    heldFailed: 'Non inviato. La risposta si è interrotta con un errore.',
    sendNow: 'Invia ora',
    sendNowHint: 'Ferma Poneva e invia ora',
    send: 'Invia',
    sendAnyway: 'Invia comunque',
    clear: 'Svuota',
    edit: 'Modifica',
    remove: 'Rimuovi',
    seePlans: 'Vedi i piani',
    added: 'Aggiunto mentre Poneva lavorava',
  },
  lv: {
    placeholder: 'Pievienojiet kaut ko vai mainiet virzienu…',
    addToWork: 'Pievienot tam, ko Poneva dara',
    attachmentsWait: 'Pielikumi tiks nosūtīti ar jūsu nākamo ziņu, kad Poneva būs beigusi',
    waitingLabel: 'Ziņas, kas gaida Ponevu',
    readNextStep: 'Poneva to izlasīs nākamajā solī',
    readNextStepMany: 'Poneva tās izlasīs nākamajā solī',
    goesNext: 'Tiks nosūtīta nākamā, kad šī atbilde būs gatava',
    goesNextMany: 'Tiks nosūtītas nākamās, kad šī atbilde būs gatava',
    sending: 'Sūta',
    notSent: 'Nav nosūtīts',
    heldQuestion: 'Gaida, līdz atbildēsiet uz jautājumu',
    heldApproval: 'Gaida, līdz izlemsiet par izmaiņām',
    heldCredits: 'Nav nosūtīts. Jums beigušies kredīti.',
    heldFailed: 'Nav nosūtīts. Atbilde apstājās ar kļūdu.',
    sendNow: 'Sūtīt tūlīt',
    sendNowHint: 'Apturēt Ponevu un sūtīt tūlīt',
    send: 'Sūtīt',
    sendAnyway: 'Tomēr sūtīt',
    clear: 'Notīrīt',
    edit: 'Rediģēt',
    remove: 'Noņemt',
    seePlans: 'Skatīt plānus',
    added: 'Pievienots, kamēr Poneva strādāja',
  },
  lt: {
    placeholder: 'Pridėkite ką nors arba pakeiskite kryptį…',
    addToWork: 'Pridėti prie to, ką daro Poneva',
    attachmentsWait: 'Priedai bus išsiųsti su kita jūsų žinute, kai Poneva baigs',
    waitingLabel: 'Ponevos laukiančios žinutės',
    readNextStep: 'Poneva tai perskaitys kitame žingsnyje',
    readNextStepMany: 'Poneva jas perskaitys kitame žingsnyje',
    goesNext: 'Bus išsiųsta kita, kai šis atsakymas bus baigtas',
    goesNextMany: 'Bus išsiųstos kitos, kai šis atsakymas bus baigtas',
    sending: 'Siunčiama',
    notSent: 'Neišsiųsta',
    heldQuestion: 'Laukia, kol atsakysite į klausimą',
    heldApproval: 'Laukia, kol nuspręsite dėl pakeitimo',
    heldCredits: 'Neišsiųsta. Baigėsi jūsų kreditai.',
    heldFailed: 'Neišsiųsta. Atsakymas sustojo dėl klaidos.',
    sendNow: 'Siųsti dabar',
    sendNowHint: 'Sustabdyti Ponevą ir siųsti dabar',
    send: 'Siųsti',
    sendAnyway: 'Vis tiek siųsti',
    clear: 'Išvalyti',
    edit: 'Redaguoti',
    remove: 'Pašalinti',
    seePlans: 'Peržiūrėti planus',
    added: 'Pridėta, kol Poneva dirbo',
  },
  mt: {
    placeholder: 'Żid xi ħaġa, jew ibdel id-direzzjoni…',
    addToWork: 'Żid ma’ dak li qed tagħmel Poneva',
    attachmentsWait: 'L-annessi jmorru mal-messaġġ li jmiss tiegħek, meta Poneva tlesti',
    waitingLabel: 'Messaġġi li qed jistennew lil Poneva',
    readNextStep: 'Poneva taqra dan fil-pass li jmiss tagħha',
    readNextStepMany: 'Poneva taqra dawn fil-pass li jmiss tagħha',
    goesNext: 'Imur wara, meta din it-tweġiba tkun lesta',
    goesNextMany: 'Imorru wara, meta din it-tweġiba tkun lesta',
    sending: 'Qed jintbagħat',
    notSent: 'Ma ntbagħatx',
    heldQuestion: 'Jistenna sakemm twieġeb il-mistoqsija',
    heldApproval: 'Jistenna sakemm tiddeċiedi dwar il-bidla',
    heldCredits: 'Ma ntbagħatx. M’għandekx aktar krediti.',
    heldFailed: 'Ma ntbagħatx. It-tweġiba waqfet bi żball.',
    sendNow: 'Ibgħat issa',
    sendNowHint: 'Waqqaf lil Poneva u ibgħat issa',
    send: 'Ibgħat',
    sendAnyway: 'Ibgħat xorta',
    clear: 'Naddaf',
    edit: 'Editja',
    remove: 'Neħħi',
    seePlans: 'Ara l-pjanijiet',
    added: 'Miżjud waqt li Poneva kienet qed taħdem',
  },
  pl: {
    placeholder: 'Dodaj coś lub zmień kierunek…',
    addToWork: 'Dodaj do tego, co robi Poneva',
    attachmentsWait: 'Załączniki pójdą z Twoją następną wiadomością, gdy Poneva skończy',
    waitingLabel: 'Wiadomości czekające na Ponevę',
    readNextStep: 'Poneva przeczyta to w następnym kroku',
    readNextStepMany: 'Poneva przeczyta je w następnym kroku',
    goesNext: 'Pójdzie jako następna, gdy ta odpowiedź będzie gotowa',
    goesNextMany: 'Pójdą jako następne, gdy ta odpowiedź będzie gotowa',
    sending: 'Wysyłanie',
    notSent: 'Nie wysłano',
    heldQuestion: 'Czeka, aż odpowiesz na pytanie',
    heldApproval: 'Czeka, aż zdecydujesz o zmianie',
    heldCredits: 'Nie wysłano. Skończyły Ci się kredyty.',
    heldFailed: 'Nie wysłano. Odpowiedź zatrzymała się z błędem.',
    sendNow: 'Wyślij teraz',
    sendNowHint: 'Zatrzymaj Ponevę i wyślij teraz',
    send: 'Wyślij',
    sendAnyway: 'Wyślij mimo to',
    clear: 'Wyczyść',
    edit: 'Edytuj',
    remove: 'Usuń',
    seePlans: 'Zobacz plany',
    added: 'Dodano, gdy Poneva pracowała',
  },
  pt: {
    placeholder: 'Acrescente algo ou mude de direção…',
    addToWork: 'Acrescentar ao que a Poneva está a fazer',
    attachmentsWait: 'Os anexos seguem com a sua próxima mensagem, quando a Poneva terminar',
    waitingLabel: 'Mensagens à espera da Poneva',
    readNextStep: 'A Poneva lê isto no próximo passo',
    readNextStepMany: 'A Poneva lê estas no próximo passo',
    goesNext: 'Segue a seguir, quando esta resposta terminar',
    goesNextMany: 'Seguem a seguir, quando esta resposta terminar',
    sending: 'A enviar',
    notSent: 'Não enviada',
    heldQuestion: 'Espera até responder à pergunta',
    heldApproval: 'Espera até decidir sobre a alteração',
    heldCredits: 'Não enviada. Ficou sem créditos.',
    heldFailed: 'Não enviada. A resposta parou com um erro.',
    sendNow: 'Enviar agora',
    sendNowHint: 'Parar a Poneva e enviar agora',
    send: 'Enviar',
    sendAnyway: 'Enviar mesmo assim',
    clear: 'Limpar',
    edit: 'Editar',
    remove: 'Remover',
    seePlans: 'Ver planos',
    added: 'Acrescentada enquanto a Poneva trabalhava',
  },
  ro: {
    placeholder: 'Adăugați ceva sau schimbați direcția…',
    addToWork: 'Adăugați la ce face Poneva',
    attachmentsWait: 'Atașamentele pleacă odată cu următorul mesaj, când Poneva termină',
    waitingLabel: 'Mesaje care așteaptă Poneva',
    readNextStep: 'Poneva va citi asta la pasul următor',
    readNextStepMany: 'Poneva le va citi la pasul următor',
    goesNext: 'Pleacă după, când acest răspuns este gata',
    goesNextMany: 'Pleacă după, când acest răspuns este gata',
    sending: 'Se trimite',
    notSent: 'Netrimis',
    heldQuestion: 'Așteaptă până răspundeți la întrebare',
    heldApproval: 'Așteaptă până decideți asupra modificării',
    heldCredits: 'Netrimis. Nu mai aveți credite.',
    heldFailed: 'Netrimis. Răspunsul s-a oprit cu o eroare.',
    sendNow: 'Trimiteți acum',
    sendNowHint: 'Opriți Poneva și trimiteți acum',
    send: 'Trimiteți',
    sendAnyway: 'Trimiteți oricum',
    clear: 'Goliți',
    edit: 'Editați',
    remove: 'Eliminați',
    seePlans: 'Vedeți planurile',
    added: 'Adăugat în timp ce Poneva lucra',
  },
  sk: {
    placeholder: 'Pridajte niečo alebo zmeňte smer…',
    addToWork: 'Pridať k tomu, čo Poneva robí',
    attachmentsWait: 'Prílohy odídu s vašou ďalšou správou, keď Poneva skončí',
    waitingLabel: 'Správy čakajúce na Ponevu',
    readNextStep: 'Poneva si to prečíta v ďalšom kroku',
    readNextStepMany: 'Poneva si ich prečíta v ďalšom kroku',
    goesNext: 'Odíde ako ďalšia, keď bude táto odpoveď hotová',
    goesNextMany: 'Odídu ako ďalšie, keď bude táto odpoveď hotová',
    sending: 'Odosielanie',
    notSent: 'Neodoslané',
    heldQuestion: 'Čaká, kým neodpoviete na otázku',
    heldApproval: 'Čaká, kým nerozhodnete o zmene',
    heldCredits: 'Neodoslané. Minuli sa vám kredity.',
    heldFailed: 'Neodoslané. Odpoveď sa zastavila s chybou.',
    sendNow: 'Odoslať hneď',
    sendNowHint: 'Zastaviť Ponevu a odoslať hneď',
    send: 'Odoslať',
    sendAnyway: 'Napriek tomu odoslať',
    clear: 'Vymazať',
    edit: 'Upraviť',
    remove: 'Odstrániť',
    seePlans: 'Zobraziť plány',
    added: 'Pridané, kým Poneva pracovala',
  },
  sl: {
    placeholder: 'Dodajte kaj ali spremenite smer…',
    addToWork: 'Dodaj k temu, kar dela Poneva',
    attachmentsWait: 'Priloge gredo z vašim naslednjim sporočilom, ko Poneva konča',
    waitingLabel: 'Sporočila, ki čakajo na Ponevo',
    readNextStep: 'Poneva bo to prebrala v naslednjem koraku',
    readNextStepMany: 'Poneva jih bo prebrala v naslednjem koraku',
    goesNext: 'Gre naslednje, ko bo ta odgovor končan',
    goesNextMany: 'Gredo naslednja, ko bo ta odgovor končan',
    sending: 'Pošiljanje',
    notSent: 'Ni poslano',
    heldQuestion: 'Čaka, da odgovorite na vprašanje',
    heldApproval: 'Čaka, da se odločite o spremembi',
    heldCredits: 'Ni poslano. Zmanjkalo vam je kreditov.',
    heldFailed: 'Ni poslano. Odgovor se je ustavil z napako.',
    sendNow: 'Pošlji zdaj',
    sendNowHint: 'Ustavi Ponevo in pošlji zdaj',
    send: 'Pošlji',
    sendAnyway: 'Vseeno pošlji',
    clear: 'Počisti',
    edit: 'Uredi',
    remove: 'Odstrani',
    seePlans: 'Oglejte si pakete',
    added: 'Dodano, medtem ko je Poneva delala',
  },
  es: {
    placeholder: 'Añade algo o cambia de dirección…',
    addToWork: 'Añadir a lo que está haciendo Poneva',
    attachmentsWait: 'Los adjuntos irán con tu próximo mensaje, cuando Poneva termine',
    waitingLabel: 'Mensajes que esperan a Poneva',
    readNextStep: 'Poneva lo leerá en su siguiente paso',
    readNextStepMany: 'Poneva los leerá en su siguiente paso',
    goesNext: 'Sale a continuación, cuando termine esta respuesta',
    goesNextMany: 'Salen a continuación, cuando termine esta respuesta',
    sending: 'Enviando',
    notSent: 'No enviado',
    heldQuestion: 'Espera hasta que respondas a la pregunta',
    heldApproval: 'Espera hasta que decidas sobre el cambio',
    heldCredits: 'No enviado. Te has quedado sin créditos.',
    heldFailed: 'No enviado. La respuesta se detuvo con un error.',
    sendNow: 'Enviar ahora',
    sendNowHint: 'Detener a Poneva y enviar ahora',
    send: 'Enviar',
    sendAnyway: 'Enviar de todos modos',
    clear: 'Borrar',
    edit: 'Editar',
    remove: 'Quitar',
    seePlans: 'Ver planes',
    added: 'Añadido mientras Poneva trabajaba',
  },
  sv: {
    placeholder: 'Lägg till något, eller ändra riktning…',
    addToWork: 'Lägg till i det Poneva gör',
    attachmentsWait: 'Bilagorna följer med ditt nästa meddelande, när Poneva är klar',
    waitingLabel: 'Meddelanden som väntar på Poneva',
    readNextStep: 'Poneva läser det här i nästa steg',
    readNextStepMany: 'Poneva läser de här i nästa steg',
    goesNext: 'Skickas härnäst, när svaret är klart',
    goesNextMany: 'Skickas härnäst, när svaret är klart',
    sending: 'Skickar',
    notSent: 'Inte skickat',
    heldQuestion: 'Väntar tills du har svarat på frågan',
    heldApproval: 'Väntar tills du har bestämt dig om ändringen',
    heldCredits: 'Inte skickat. Dina krediter är slut.',
    heldFailed: 'Inte skickat. Svaret stoppades av ett fel.',
    sendNow: 'Skicka nu',
    sendNowHint: 'Stoppa Poneva och skicka nu',
    send: 'Skicka',
    sendAnyway: 'Skicka ändå',
    clear: 'Rensa',
    edit: 'Redigera',
    remove: 'Ta bort',
    seePlans: 'Se planer',
    added: 'Tillagt medan Poneva arbetade',
  },
};
export default steerCopy;
